import { isSameDay } from '@myre/common-dates';

import { DatePipe } from '@angular/common';
import { inject, Injectable } from '@angular/core';

import {
  type ChatStatus,
  type MyreUiMessage,
} from '../interfaces/chat.interfaces';

@Injectable({ providedIn: 'root' })
export class MessageTimestampService {
  private readonly _datePipe = inject(DatePipe);
  private readonly _timestamps = new Map<string, Date>();
  private _previousStatus: ChatStatus = 'ready';

  /**
   * Processes messages and assigns timestamps based on role:
   * - User messages: immediate timestamp
   * - Assistant messages: timestamp on streaming completion
   */
  public processMessages(
    messages: MyreUiMessage[],
    currentStatus: ChatStatus,
  ): void {
    this._assignUserTimestamps(messages);
    this._assignAssistantTimestampOnCompletion(messages, currentStatus);
    this._previousStatus = currentStatus;
  }

  /**
   * Gets the timestamp for a message.
   */
  public getTimestamp(messageId: string): Date | null {
    return this._timestamps.get(messageId) ?? null;
  }

  /**
   * Sets a timestamp for a given message ID.
   */
  public setTimestamp(messageId: string, date: Date = new Date()): void {
    this._timestamps.set(messageId, date);
  }

  /**
   * Formats the timestamp in a user-friendly format.
   * Shows time only (HH:mm) for today's messages,
   * includes date for older messages (e.g., "Jan 19, 14:32").
   */
  public formatTimestamp(messageId: string): string | null {
    const timestamp = this.getTimestamp(messageId);

    if (!timestamp) {
      return null;
    }

    const isToday = isSameDay(timestamp, new Date());
    const format = isToday ? 'HH:mm' : 'MMM d, HH:mm';

    return this._datePipe.transform(timestamp, format) ?? '';
  }

  /**
   * Assigns timestamps to user messages immediately.
   */
  private _assignUserTimestamps(messages: readonly MyreUiMessage[]): void {
    for (const message of messages) {
      if (message.role === 'user' && !this._timestamps.has(message.id)) {
        this._timestamps.set(message.id, new Date());
      }
    }
  }

  /**
   * Assigns timestamps to assistant messages when streaming completes.
   */
  private _assignAssistantTimestampOnCompletion(
    messages: readonly MyreUiMessage[],
    currentStatus: ChatStatus,
  ): void {
    const streamingJustCompleted =
      this._previousStatus === 'streaming' && currentStatus !== 'streaming';

    if (streamingJustCompleted) {
      const lastMessage = messages.at(-1);
      const isUntimedAssistantMessage =
        lastMessage?.role === 'assistant' &&
        !this._timestamps.has(lastMessage.id);

      if (isUntimedAssistantMessage) {
        this._timestamps.set(lastMessage.id, new Date());
      }
    }
  }
}
